<script setup lang="ts">
import { connectSearchBox } from 'instantsearch.js/es/connectors';
import {
  SearchBoxConnectorParams,
  SearchBoxWidgetDescription,
} from 'instantsearch.js/es/connectors/search-box/connectSearchBox';
import { computed, useTemplateRef, watch } from 'vue';

import { useWidget } from '@/composables/instant-search/useWidget';

const { placeholder = 'Search...' } = defineProps<{
  placeholder?: string;
}>();

const query = defineModel<string>();

const input = useTemplateRef('input');

const { state } = useWidget<SearchBoxWidgetDescription, SearchBoxConnectorParams>(
  { connector: connectSearchBox },
  {},
);

const shouldShowLoadingIndicator = computed(() => state.value?.isSearchStalled ?? false);

watch(
  query,
  (newQuery) => {
    state.value?.refine(newQuery ?? '');
  },
  { flush: 'post' },
);
</script>

<template>
  <div class="ais-SearchBox" data-test="searchBox">
    <form
      action=""
      class="ais-SearchBox-form"
      novalidate
      role="search"
      @submit.prevent="input?.blur()"
      @reset.prevent="query = ''"
    >
      <input
        v-model="query"
        autocapitalize="off"
        autocomplete="off"
        autocorrect="off"
        aria-label="Search"
        class="ais-SearchBox-input"
        maxlength="512"
        :placeholder
        ref="input"
        spellcheck="false"
        type="search"
      />
      <button
        class="ais-SearchBox-submit"
        :hidden="shouldShowLoadingIndicator"
        title="Submit the search query"
        type="submit"
      >
        <svg
          aria-label="magnify glass image"
          class="mt-1"
          height="14"
          role="img"
          viewBox="0 0 40 40"
          width="14"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M26.804 29.01c-2.832 2.34-6.465 3.746-10.426 3.746C7.333 32.756 0 25.424 0 16.378 0 7.333 7.333 0 16.378 0c9.046 0 16.378 7.333 16.378 16.378 0 3.96-1.406 7.594-3.746 10.426l10.534 10.534c.607.607.61 1.59-.004 2.202-.61.61-1.597.61-2.202.004L26.804 29.01zm-10.426.627c7.323 0 13.26-5.936 13.26-13.26 0-7.32-5.937-13.257-13.26-13.257C9.056 3.12 3.12 9.056 3.12 16.378c0 7.323 5.936 13.26 13.258 13.26z"
            fillRule="evenodd"
          />
        </svg>
      </button>
      <button
        class="ais-SearchBox-reset"
        :hidden="!query || shouldShowLoadingIndicator"
        title="Clear the search query"
        type="reset"
      >
        <svg
          aria-label="reset button image"
          class="mt-1"
          height="12"
          role="img"
          viewBox="0 0 20 20"
          width="12"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z"
            fillRule="evenodd"
          />
        </svg>
      </button>

      <span class="ais-SearchBox-loadingIndicator" :hidden="!shouldShowLoadingIndicator">
        <slot name="loading-indicator">
          <svg
            :aria-hidden="!shouldShowLoadingIndicator"
            aria-label="Results are loading"
            class="ais-SearchBox-loadingIcon"
            height="16"
            stroke="#444"
            viewBox="0 0 38 38"
            width="16"
          >
            <g fill="none" fill-rule="evenodd">
              <g transform="translate(1 1)" stroke-width="2">
                <circle stroke-opacity=".5" cx="18" cy="18" r="18" />
                <path d="M36 18c0-9.94-8.06-18-18-18">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 18 18"
                    to="360 18 18"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </path>
              </g>
            </g>
          </svg>
        </slot>
      </span>
    </form>
  </div>
</template>
